import { FormattedMessage, useIntl } from "react-intl";
import { InlineApiError } from "../../../shared/Shared";
import { YamlView } from "../../../shared/yaml/YamlView";
import { templateColor, templateLetter } from "../gallery/TemplateCard";
import {
  type FacetLabels,
  facetLabels,
  TemplateFacetChips,
  type TemplateFacetFields,
  TemplateKindLine,
  TemplateTeamSections,
} from "./TemplateDetailFacets";

/**
 * The V1 template-detail card (avatar header, description, chips, AGENT YAML pane, "Use this
 * template →") extracted verbatim from the MFA gallery modal. Pure presentation: the route
 * (modal/standalone) feeds it data and the use-command. Every template is open to every
 * viewer, so there is no Ready/Locked pill, plan banner or model picker: the server never
 * sent the verdicts they read (workflows/V5-Template-Method-Review.md V5, L6). Under the name
 * it says team or single; under the description, the gallery card's labelled chips; then a
 * team's members or the teams a single sits in. It never names the library
 * (workflows/V5-Template-Organization.md Rev 2 §R2.2).
 */

const ink = "var(--brand-ink)";
const card = "var(--brand-card)";

export type TemplateDetailData = TemplateFacetFields & {
  name: string;
  description: string;
};

const NO_LABELS = facetLabels(undefined);

export function TemplateDetailView({
  template,
  labels = NO_LABELS,
  yaml,
  onUse,
  usePending,
  onClose,
  error,
}: {
  template: TemplateDetailData;
  /** Facet labels from the shared vocabulary; a key without one shows as itself. */
  labels?: FacetLabels;
  /** undefined hides the AGENT YAML pane; null = loading. */
  yaml?: string | null;
  onUse: () => void;
  usePending: boolean;
  onClose?: () => void;
  error?: unknown;
}) {
  const intl = useIntl();
  return (
    <div
      role="dialog"
      aria-modal={onClose ? "true" : undefined}
      aria-label={template.name}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "860px",
        maxHeight: onClose ? "88vh" : undefined,
        overflow: onClose ? "hidden" : "visible",
        background: card,
        border: `var(--brand-bw-2-5) solid ${ink}`,
        borderRadius: "var(--brand-r-22)",
        boxShadow: "var(--brand-shadow-8)",
        padding: "26px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "13px", flexShrink: 0 }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            flex: "none",
            border: `var(--brand-bw-2-5) solid ${ink}`,
            borderRadius: "var(--brand-r-13)",
            boxShadow: "var(--brand-shadow-3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "22px",
            color: card,
            background: templateColor(template.categoryKey, labels.categoryOrder),
          }}
        >
          {templateLetter(template.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            className="mfa-clip"
            style={{
              margin: 0,
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "20px",
            }}
          >
            {template.name}
          </h2>
          <TemplateKindLine
            template={template}
            className="mfa-clip"
            style={{ fontWeight: 700, fontSize: "12.5px", color: "var(--brand-muted)" }}
          />
        </div>
        {onClose ? (
          <button
            type="button"
            aria-label={intl.formatMessage({ id: "common.close" })}
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              flex: "none",
              background: "var(--brand-x-f4efe2)",
              border: `var(--brand-bw-2-5) solid ${ink}`,
              borderRadius: "var(--brand-r-9)",
              cursor: "pointer",
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "15px",
              color: ink,
            }}
          >
            ✕
          </button>
        ) : null}
      </div>
      <div
        style={{
          fontWeight: 600,
          fontSize: "14px",
          color: "var(--brand-muted-2)",
          lineHeight: 1.55,
          marginTop: "14px",
          flexShrink: 0,
        }}
      >
        {template.description}
      </div>
      <TemplateFacetChips
        template={template}
        labels={labels}
        style={{ marginTop: "12px", flexShrink: 0 }}
      />
      <TemplateTeamSections template={template} headingLevel={3} />
      {yaml !== undefined ? (
        <>
          <div
            style={{
              fontFamily: "var(--brand-font-display)",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "1px",
              color: "var(--brand-muted)",
              margin: "18px 0 8px",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            <FormattedMessage id="tpl.agentYaml" />
          </div>
          <div
            style={{
              flex: "0 1 auto",
              minHeight: 0,
              background: "var(--brand-ink-deep)",
              border: `var(--brand-bw-2-5) solid ${ink}`,
              borderRadius: "var(--brand-r-14)",
              overflow: "hidden",
            }}
          >
            <YamlView
              value={yaml ?? "…"}
              ariaLabel={intl.formatMessage({ id: "tpl.agentYaml" })}
              height={260}
              dark
            />
          </div>
        </>
      ) : null}
      <div style={{ flexShrink: 0 }}>
        <button
          type="button"
          disabled={usePending}
          onClick={onUse}
          style={{
            display: "block",
            width: "100%",
            textAlign: "center",
            marginTop: "18px",
            padding: "13px",
            background: "var(--brand-green)",
            border: `var(--brand-bw-2-5) solid ${ink}`,
            borderRadius: "var(--brand-r-14)",
            boxShadow: "var(--brand-shadow-4)",
            cursor: usePending ? "default" : "pointer",
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "16px",
            color: card,
          }}
        >
          <FormattedMessage id="tpl.useTemplate" />
        </button>
        <InlineApiError error={error} />
      </div>
    </div>
  );
}
